import React, { useState } from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { getIn, useFormikContext } from 'formik';

import CancelButton from '~/components/core/Buttons/CancelButton';
import { uploadStoredFile } from '~/components/Documents/UploadDocumentsCard/utils';
import FileDropZone from '~/components/FileDropZone';
import type {
  UploadCallbackResponse,
  UploadedFileData,
  VoiceFileUploaderFormikProps,
  VoicePlayerProps,
} from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageConfigurationDialog/types';
import { reportGenericException } from '~/Utils';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

const VoicePlayer: React.FC<VoicePlayerProps> = ({ audioSrc }) => {
  if (!audioSrc) return null;

  return <audio data-testid="voice-player" className="my-20 h-40" controls src={audioSrc} />;
};

function VoiceFileUploaderFormik({
  voiceFileInputId,
  existingVoiceFileUrl,
  onReplaceExistingRecording,
  urlPrefix,
  storedFileIdPath,
  useStoredFileIdPathAsErrorField = false,
}: VoiceFileUploaderFormikProps): JSX.Element {
  const [audioSrc, setAudioSrc] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string>('');

  const { errors, touched, setFieldValue, values, setSubmitting, isSubmitting } = useFormikContext();
  const [voiceFile, setVoiceFile] = useState<File | undefined>(undefined);
  const [replaceExistingVoiceFile, setReplaceExistingVoiceFile] = React.useState<boolean>(false);

  const handleFileSelect = async (file: File): Promise<void> => {
    setSubmitting(true);
    setVoiceFile(file);
    setUploadError('');
    setUploadProgress(0);
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (event.target?.result) {
        setAudioSrc(event.target.result as string);
      }
    };
    reader.readAsDataURL(file);

    try {
      const onUpdatePercent = (progressEvent: ProgressEvent): void => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      };

      const storageFileName = await uploadStoredFile({
        urlPrefix,
        file,
        onUpdatePercent,
      });

      const { data }: UploadCallbackResponse = await axios.post(`${urlPrefix}/upload_stored_file_finished_callback`, {
        storage_filename: storageFileName,
        file_type: file.type,
        file_name: file.name,
        file_size: file.size,
        last_modified: new Date().toISOString(),
        type: file.type,
        document_name: file.name,
        document_date: new Date().toISOString(),
        exposure_ids: [],
        summary: 'voice recording file',
      });

      const uploadedFileData: UploadedFileData = {
        stored_file_id: Number(data),
        storage_file_name: storageFileName || '',
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
      };

      setFieldValue(voiceFileInputId, uploadedFileData);
      if (storedFileIdPath) {
        setFieldValue(storedFileIdPath, uploadedFileData.stored_file_id);
      }
    } catch (error) {
      const errorMessage =
        getNotifyUserMessage(error) ?? ((error as AxiosError)?.response?.data?.message || 'Upload failed');
      setUploadError(errorMessage);
      reportGenericException(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelFileSelect = (): void => {
    setVoiceFile(undefined);
    setAudioSrc('');
    setUploadProgress(0);
    setUploadError('');
    setFieldValue(voiceFileInputId, undefined);
    if (storedFileIdPath) {
      setFieldValue(storedFileIdPath, undefined);
    }
  };

  if (existingVoiceFileUrl && !replaceExistingVoiceFile) {
    return (
      <div className="flex flex-col items-center justify-center">
        <VoicePlayer audioSrc={existingVoiceFileUrl} />
        <CancelButton
          onClick={() => {
            setReplaceExistingVoiceFile(true);
            onReplaceExistingRecording();
          }}
          content="Replace Existing Recording"
        />
      </div>
    );
  }

  const hasVoiceFileInputError = getIn(errors, voiceFileInputId) && getIn(touched, voiceFileInputId);
  const hasStoredFileIdError = storedFileIdPath && useStoredFileIdPathAsErrorField && getIn(errors, storedFileIdPath);
  const voiceFileInputErrorMessage = getIn(errors, voiceFileInputId);
  const storedFileIdErrorMessage =
    storedFileIdPath && useStoredFileIdPathAsErrorField ? getIn(errors, storedFileIdPath) : undefined;

  return (
    <FileDropZone
      uploadPercent={uploadProgress}
      onFileSelect={handleFileSelect}
      onCancelFileSelect={handleCancelFileSelect}
      file={voiceFile}
      fileDisplayComponent={
        <div className="flex items-center justify-center">
          {getIn(values, voiceFileInputId) ? (
            <VoicePlayer audioSrc={audioSrc} />
          ) : (
            <span className="flex h-80 items-center justify-center">Uploading...</span>
          )}
        </div>
      }
      error={hasVoiceFileInputError || !!uploadError || hasStoredFileIdError}
      errorMessage={uploadError || voiceFileInputErrorMessage || storedFileIdErrorMessage}
      disabled={isSubmitting}
      dropZoneOptions={{ accept: '.mp3,.wave' }}
      baseStyleOverride={{
        width: '100%',
        height: '100%',
        paddingTop: '25px',
        paddingBottom: '25px',
      }}
      gridContainerAlignItems="stretch"
      inputProps={{ 'data-testid': 'voice-file-input' }}
    />
  );
}

export default VoiceFileUploaderFormik;
